import React, { useEffect, useState } from "react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { OrderItem } from "../../services/types/orders.types";
import {
    getRestoreOrderPreviewRequest,
    restoreOrderRequest,
    RestorePreview,
    RestoreSeatInfo,
} from "../../services/api/orders.api";

interface RestoreOrderModalProps {
    order: OrderItem | null;
    onClose: () => void;
    onRestored: (updated: OrderItem) => void;
}

// Подпись места: «Тур, Город, дата — Сектор / Ряд / Место» или стоячее место.
const seatLabel = (seat: RestoreSeatInfo): string => {
    const place = seat.is_standing
        ? `Стоячее место${seat.standing_name ? ` (${seat.standing_name})` : ""}`
        : `${seat.sector_name || "—"} / ряд ${seat.row_name || "—"} / место ${seat.seat_name || "—"}`;
    return place;
};

const eventLabel = (seat: RestoreSeatInfo): string => {
    const date = seat.start_at ? new Date(seat.start_at).toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }) : "";
    return [seat.tour_name, seat.city_name, date].filter(Boolean).join(", ");
};

/**
 * Восстановление удалённого заказа.
 * Сначала загружает предпросмотр: если все места свободны — можно восстановить тот же заказ
 * (тот же номер, новый срок и новые штрихкоды). Если места заняты или заказ восстановить
 * нельзя (партнёрский, возврат, мероприятие прошло) — показывает уведомление, что нужно
 * создать новый заказ вручную.
 */
const RestoreOrderModal: React.FC<RestoreOrderModalProps> = ({ order, onClose, onRestored }) => {
    const [preview, setPreview] = useState<RestorePreview | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadPreview = async (orderId: string) => {
        setIsLoading(true);
        try {
            const res = await getRestoreOrderPreviewRequest(orderId);
            setPreview(res);
        } catch (err: any) {
            setError(err?.message || "Не удалось проверить места заказа");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setPreview(null);
        setError(null);
        if (order) void loadPreview(order.id);
    }, [order]);

    if (!order) return null;

    const handleRestore = async () => {
        setIsRestoring(true);
        setError(null);
        try {
            const res = await restoreOrderRequest(order.id);
            onRestored(res.order);
        } catch (err: any) {
            // Места могли занять, пока окно было открыто, — обновляем список.
            setError(err?.message || "Не удалось восстановить заказ");
            await loadPreview(order.id);
        } finally {
            setIsRestoring(false);
        }
    };

    const takenSeats = (preview?.seats || []).filter((s) => !s.available);
    const seatsTaken = takenSeats.length > 0;
    const blockReason = preview?.block_reason || null;
    const canRestore = Boolean(preview?.can_restore);

    return (
        <Modal
            isOpen={!!order}
            title={
                preview && !canRestore && seatsTaken && !blockReason
                    ? `Места уже куплены — заказ ${order.order_number}`
                    : `Восстановить заказ ${order.order_number}?`
            }
            onClose={() => !isRestoring && onClose()}
            width="md"
        >
            {isLoading && !preview ? (
                <p className="card-text">Проверяем, свободны ли места заказа...</p>
            ) : preview ? (
                <div className="restore-order">
                    {blockReason ? (
                        <div className="form-global-error">{blockReason.message}</div>
                    ) : seatsTaken ? (
                        <div className="form-global-error">
                            Места заказа уже куплены или забронированы ({takenSeats.length} из {preview.seats.length}).
                            Восстановить этот заказ нельзя — создайте новый заказ вручную.
                        </div>
                    ) : (
                        <p className="card-text">
                            Все места свободны. Заказ будет восстановлен с тем же номером и суммой, получит новый
                            срок оплаты (как только что оформленный), а места снова закрепятся за ним.
                            {preview.is_paid
                                ? " Заказ оплачен — у билетов будут новые штрихкоды, старые билеты станут недействительны. Отправьте клиенту билеты заново."
                                : ""}
                        </p>
                    )}

                    {preview.legacy_snapshot && !blockReason && (
                        <div className="restore-order-note">
                            Заказ удалён до обновления системы: список мест взят из всей истории заказа и может
                            включать билеты, удалённые из заказа по одному. Проверьте места перед восстановлением.
                        </div>
                    )}

                    {preview.seats.length > 0 && (
                        <ul className="restore-order-seats">
                            {preview.seats.map((seat) => (
                                <li
                                    key={seat.hall_seat_price_id}
                                    className={`restore-order-seat ${seat.available ? "" : "restore-order-seat-taken"}`}
                                >
                                    <div>
                                        <div className="restore-order-seat-place">{seatLabel(seat)}</div>
                                        <div className="table-secondary-text">{eventLabel(seat) || "—"}</div>
                                    </div>
                                    <span
                                        className={`status-badge ${seat.available ? "status-badge-active" : "status-badge-inactive"}`}
                                        title={seat.reason_text || undefined}
                                    >
                                        {seat.available ? "Свободно" : seat.reason_text || "Занято"}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {error && <div className="form-global-error">{error}</div>}
                </div>
            ) : error ? (
                <div className="form-global-error">{error}</div>
            ) : null}

            <div className="modal-actions">
                {canRestore ? (
                    <>
                        <Button type="button" variant="primary" onClick={handleRestore} disabled={isRestoring || isLoading}>
                            {isRestoring ? "Восстанавливаем..." : "Восстановить заказ"}
                        </Button>
                        <Button type="button" variant="ghost" onClick={onClose} disabled={isRestoring}>
                            Отмена
                        </Button>
                    </>
                ) : (
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Понятно
                    </Button>
                )}
            </div>
        </Modal>
    );
};

export default RestoreOrderModal;
